//ListProduct.jsx
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addToCart } from "store/slices/cartSlice";
import {
  addToFavorites,
  removeFromFavorites,
} from "store/slices/favoritesSlice";

import { getCurrentPrice, useQuantityHandlers } from "config/config";
import useProductById from "hooks/useProductById";
import { getProductImageUrls } from "utils/productImages";

import ProductInListGrid from "./ProductInListGrid";
import ProductInListRow from "./ProductInListRow";
import {
  badgeFor,
  plainDescription,
  stockState,
  unitLabel,
} from "./productPresentation";

import { QuickLookDialog } from "@/design-system";

/**
 * One product, rendered as a card or a row, with its own quantity, cart and quick look.
 *
 * **Quick look lives here rather than in the listings**, which differs from the
 * reference — it mounts a single dialog at the app root and tracks a `quickLookPid`.
 * Here every product surface already funnels through this component: two listings *and*
 * the Best Sellers and Recently Viewed rails. Owning it here gives all four quick look
 * with no wiring, where hoisting it would mean four owners each rebuilding the view
 * model and an add-to-cart handler. The cost is one dialog element per card, which is
 * why the view model is built lazily below.
 */
// Quick look is HIDDEN for now (on request), not removed: flipping this to true
// restores the whole feature — the card's glass pill (it renders only when
// `onQuickLook` is passed) and the per-card dialog. Everything downstream
// (`QuickLookDialog`, the lazy detail fetch, the add handler) is intact.
const QUICK_LOOK_ENABLED = false;

export default function ListProduct({ product, listType = "grid" }) {
  const { id } = product;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.user.info);
  const favorites = useSelector((state) => state.favorites.items);
  const [showQuickLook, setShowQuickLook] = useState(false);

  // The listing payload is `_getStandardFields()`, which has no description. Fetch the
  // full record — `_getExtendedFields()` — but only while the dialog is open, or a
  // 48-card grid would fire 48 requests for dialogs nobody opened.
  const { product: detail } = useProductById(id, showQuickLook);

  // `increment` / `decrement` are deliberately unused: both views render the DS
  // QuantityStepper, which is controlled by value alone and clamps internally.
  // `actualQuantity` still matters — it's the buy-X-get-Y total that goes to the cart.
  const { quantity, actualQuantity, handleQuantityChange } =
    useQuantityHandlers(1, product?.stockdescription);

  const handleAddToCart = () => {
    const cartItem = {
      id: product.id,
      itemid: product.itemid,
      quantity: Number(actualQuantity),
      userId: userInfo?.id,
      selected: true,
      custrecord_ci_sub_enabled: false,
      custrecord_ci_sub_interval: null,
      custrecord_ci_sub_unit: product.custrecord_ci_sub_unit || "months",
    };
    dispatch(addToCart(cartItem));
  };

  const handleNavigate = () => navigate(`/product/${id}`);

  /**
   * The dialog takes a small view model, not a NetSuite record. Called only while the
   * dialog is open: `getProductImageUrls` runs `JSON.parse` over
   * `custitem_product_images` and `getCurrentPrice` walks the pricing tiers, and doing
   * either for all 48 cards on every render is pure waste.
   *
   * `rating`, `reviews` and `description` are omitted because the listing payload has
   * none of them — the dialog hides each slot whose data is absent. Price is computed at
   * quantity 1 because the dialog carries its own stepper.
   */
  const buildQuickLookProduct = () => {
    // Detail wins where it has a field, listing fills the rest, so the dialog opens
    // immediately with what the grid already knows and enriches when the fetch lands
    // instead of showing a spinner. `findById` runs `addPricingToItems` too, so tier
    // pricing survives either way.
    const full = detail ? { ...product, ...detail } : product;
    const { basePrice, currentPrice, savePrice } = getCurrentPrice(
      full,
      1,
      userInfo?.category
    );
    const isFavorite = favorites.includes(Number(id));
    return {
      name: full.itemid,
      brand: full.manufacturer,
      sku: full.mpn || undefined,
      images: getProductImageUrls(full),
      price: currentPrice,
      originalPrice: savePrice > 0 ? basePrice : undefined,
      unit: unitLabel(full),
      stock: stockState(full),
      badge: badgeFor(full),
      description: plainDescription(full),
      isSaved: isFavorite,
      onFavorite: () =>
        dispatch(
          isFavorite
            ? removeFromFavorites(Number(id))
            : addToFavorites(Number(id))
        ),
    };
  };

  // The dialog adds the quantity chosen inside it, which is independent of the card's.
  const handleQuickLookAdd = (qty) => {
    dispatch(
      addToCart({
        id: product.id,
        itemid: product.itemid,
        quantity: Number(qty) || 1,
        userId: userInfo?.id,
        selected: true,
        custrecord_ci_sub_enabled: false,
        custrecord_ci_sub_interval: null,
        custrecord_ci_sub_unit: product.custrecord_ci_sub_unit || "months",
      })
    );
  };

  const quickLook = QUICK_LOOK_ENABLED && showQuickLook && (
    <QuickLookDialog
      open
      onClose={() => setShowQuickLook(false)}
      product={buildQuickLookProduct()}
      onAdd={handleQuickLookAdd}
    />
  );

  // Only the grid card has a quick-look affordance; the reference's list row has no slot
  // for one, and its whole width is already a navigate target.
  if (listType === "grid") {
    return (
      <>
        <ProductInListGrid
          product={product}
          quantity={quantity}
          actualQuantity={actualQuantity}
          handleQuantityChange={handleQuantityChange}
          handleAddToCart={handleAddToCart}
          handleNavigate={handleNavigate}
          onQuickLook={
            QUICK_LOOK_ENABLED ? () => setShowQuickLook(true) : undefined
          }
        />
        {quickLook}
      </>
    );
  }

  return (
    <ProductInListRow
      product={product}
      quantity={quantity}
      handleQuantityChange={handleQuantityChange}
      handleAddToCart={handleAddToCart}
      handleNavigate={handleNavigate}
    />
  );
}
